import type { EventFieldSchema } from "./event-record-schema";

/**
 * Pure (React-free) timing maths for the fire-drill event type. Kept separate
 * from `fire-drill.tsx` so it can be unit-tested without a DOM/component
 * runtime. The two derived timings are display-only — computed from the
 * captured times, never stored on the immutable record.
 */

export interface DerivedTimingRow {
  key: string;
  label: string;
  /** Formatted duration (e.g. "2m 30s"), or null when it can't be computed. */
  value: string | null;
}

/** Parse a "HH:MM" or "HH:MM:SS" time-of-day string into seconds since midnight. */
function timeOfDayToSeconds(value: unknown): number | null {
  if (typeof value !== "string" || value.trim() === "") return null;
  const parts = value.split(":").map((p) => Number(p));
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return null;
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  const seconds = parts[2] ?? 0;
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Format a non-negative duration in seconds as e.g. "2m 30s" / "1h 5m" / "45s".
 * Zero-valued leading units are dropped; a zero duration renders as "0s".
 */
export function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(" ");
}

/**
 * The two calculated fire-drill timings — both measured from the alarm sounding.
 * Returns rows only when the schema actually carries the source fields, so this
 * is a no-op (empty array) for every non-fire-drill event type and the read
 * views can call it unconditionally. A `null` value means one of the operands
 * is missing or the times are inconsistent (end before start).
 */
export function deriveTimingRows(
  schema: EventFieldSchema,
  data: Record<string, unknown>,
): DerivedTimingRow[] {
  const keys = new Set(schema.fields.map((f) => f.key));

  const elapsed = (fromKey: string, toKey: string): string | null => {
    const from = timeOfDayToSeconds(data[fromKey]);
    const to = timeOfDayToSeconds(data[toKey]);
    if (from === null || to === null || to < from) return null;
    return formatDuration(to - from);
  };

  const rows: DerivedTimingRow[] = [];
  if (keys.has("timeAlarmRaised") && keys.has("timeEvacuated")) {
    rows.push({
      key: "evacuationTimeToAllExit",
      label: "Evacuation time to all exit",
      value: elapsed("timeAlarmRaised", "timeEvacuated"),
    });
  }
  if (keys.has("timeAlarmRaised") && keys.has("timeOfRollCall")) {
    rows.push({
      key: "timeToRollCall",
      label: "Time to roll call",
      value: elapsed("timeAlarmRaised", "timeOfRollCall"),
    });
  }
  return rows;
}
